import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

function optionValue(opt) {
  if (typeof opt === 'string') return opt;
  return opt?.value ?? opt?.label ?? '';
}

function optionLabel(opt) {
  if (typeof opt === 'string') return opt;
  return opt?.label ?? opt?.value ?? '';
}

/**
 * Renders fields from backend preference_schema or request_schema.
 */
export default function SchemaForm({ schema = [], values = {}, onChange, prefix = 'field' }) {
  if (!schema?.length) {
    return null;
  }

  const setField = (key, value) => {
    onChange({ ...values, [key]: value });
  };

  return (
    <div className="space-y-3">
      {schema.map((field) => {
        const key = field.key;
        const value = values[key];
        const testId = `${prefix}-${key}`;

        if (field.type === 'enum') {
          return (
            <div key={key} className="space-y-1">
              <Label className="text-sm text-slate-700">{field.label}</Label>
              <select
                data-testid={testId}
                value={value ?? ''}
                onChange={(e) => setField(key, e.target.value || undefined)}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
              >
                <option value="">No preference</option>
                {(field.options || []).map((opt) => (
                  <option key={optionValue(opt)} value={optionValue(opt)}>
                    {optionLabel(opt)}
                  </option>
                ))}
              </select>
            </div>
          );
        }

        if (field.type === 'multi_enum') {
          const selected = Array.isArray(value) ? value : [];
          return (
            <div key={key} className="space-y-1">
              <Label className="text-sm text-slate-700">{field.label}</Label>
              <div className="flex flex-wrap gap-2" data-testid={testId}>
                {(field.options || []).map((opt) => {
                  const v = optionValue(opt);
                  const active = selected.includes(v);
                  return (
                    <button
                      key={v}
                      type="button"
                      onClick={() => {
                        const next = active
                          ? selected.filter((x) => x !== v)
                          : [...selected, v];
                        setField(key, next.length ? next : undefined);
                      }}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                        active
                          ? 'bg-blue-700 text-white border-blue-700'
                          : 'bg-white text-slate-700 border-slate-200'
                      }`}
                    >
                      {optionLabel(opt)}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        }

        if (field.type === 'number') {
          return (
            <div key={key} className="space-y-1">
              <Label className="text-sm text-slate-700">{field.label}</Label>
              <Input
                data-testid={testId}
                type="number"
                min={field.min_value ?? 0}
                value={value ?? ''}
                onChange={(e) => {
                  const raw = e.target.value;
                  setField(key, raw === '' ? undefined : Number(raw));
                }}
              />
            </div>
          );
        }

        if (field.type === 'textarea') {
          return (
            <div key={key} className="space-y-1">
              <Label className="text-sm text-slate-700">{field.label}</Label>
              <Textarea
                data-testid={testId}
                placeholder={field.placeholder || ''}
                value={value ?? ''}
                onChange={(e) => setField(key, e.target.value || undefined)}
                rows={3}
              />
            </div>
          );
        }

        return (
          <div key={key} className="space-y-1">
            <Label className="text-sm text-slate-700">{field.label}</Label>
            <Input
              data-testid={testId}
              value={value ?? ''}
              onChange={(e) => setField(key, e.target.value || undefined)}
            />
          </div>
        );
      })}
    </div>
  );
}
